import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { notifyUser } from '@/store/Toast';
import { createPublicIntake } from './api/shared';
import { SectionPrimaryObjectOfWork } from './components/form-sections/section-primary-object-of-work.tsx';
import { WizardLayout } from './components/WizardLayout';
import { useIntakeForm } from './hooks/useIntakeForm';
import { buildSteps, getBreadcrumbGroup, hasDiscoveryFocus, isEntryContactComplete, type StepItem } from './hooks/wizard-helpers';
import { markStepComplete, setCurrentStep, setIntakeId } from './state/governmentIntakeSlice';
import { selectCurrentStep } from './state/selectors';
import type { ValidationChange } from './types/base-entity';

export function WizardView() {
  const { data, getPayload } = useIntakeForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentStep = useSelector(selectCurrentStep);
  const setCurrentStepIndex = useCallback(
    (index: number) => {
      dispatch(setCurrentStep(index));
    },
    [dispatch]
  );

  const [sectionValidity, setSectionValidity] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const lastSubmittedPayloadRef = useRef<string | null>(null);

  const PrimaryObjectOfWork = useCallback(({ onValidationChange }: ValidationChange) => {
    return <SectionPrimaryObjectOfWork onValidationChange={onValidationChange} />;
  }, []);

  const steps = useMemo<StepItem[]>(() => buildSteps(data, PrimaryObjectOfWork), [data, PrimaryObjectOfWork]);

  useEffect(() => {
    if (steps.length > 0 && currentStep >= steps.length) {
      dispatch(setCurrentStep(Math.max(0, steps.length - 1)));
    }
  }, [steps.length, currentStep, dispatch]);

  const active = steps[currentStep];
  const isValid = sectionValidity[active.key];
  const entryContactComplete = isEntryContactComplete(data.entryContact);
  const discoveryFocusComplete = hasDiscoveryFocus(data.primaryObjectOfWork);
  const canSubmitDiscovery = entryContactComplete && discoveryFocusComplete;

  const progressPercentage = useMemo(
    () => (steps.length > 0 ? Math.round(((currentStep + 1) / steps.length) * 100) : 0),
    [currentStep, steps.length]
  );

  const breadcrumbGroup = useMemo(() => getBreadcrumbGroup(currentStep, steps, active.key), [active.key, currentStep, steps]);

  const handleValidationChange = useCallback(
    (valid: boolean) => {
      setSectionValidity((v) => ({ ...v, [active.key]: valid }));
    },
    [active.key]
  );

  const handleEdit = useCallback(
    (sectionKey: string) => {
      const idx = steps.findIndex((s) => s.key === sectionKey);
      if (idx >= 0) setCurrentStepIndex(idx);
    },
    [steps, setCurrentStepIndex]
  );

  const handleSaveAndExit = useCallback(() => {
    dispatch(notifyUser({ showToast: true, toastMessage: 'Progress saved successfully', toastType: 'success' }));
  }, [dispatch]);

  const handleNext = useCallback(() => {
    const advanceToNextStep = () => {
      dispatch(markStepComplete(active.key));
      setCurrentStepIndex(Math.min(steps.length - 1, currentStep + 1));
    };

    if (active.key !== 'review') {
      advanceToNextStep();
      return;
    }

    const payload = getPayload();
    const payloadString = JSON.stringify(payload);

    if (lastSubmittedPayloadRef.current !== null && lastSubmittedPayloadRef.current === payloadString) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'No changes to submit. Discovery was already submitted with this information.',
          toastType: 'info',
        })
      );
      return;
    }

    setSubmitting(true);
    createPublicIntake({ payload: payload as Record<string, unknown>, status: 'SUBMITTED', source: 'PUBLIC' })
      .then((res) => {
        lastSubmittedPayloadRef.current = payloadString;
        if (res?.data?.id != null) {
          dispatch(setIntakeId(String(res.data.id)));
        }
        dispatch(notifyUser({ showToast: true, toastMessage: 'Discovery submitted successfully', toastType: 'success' }));
        advanceToNextStep();
        navigate('/government-intake/completion');
      })
      .catch(() => {
        dispatch(notifyUser({ showToast: true, toastMessage: 'Failed to submit discovery. Please try again.', toastType: 'error' }));
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [active.key, currentStep, dispatch, getPayload, navigate, setCurrentStepIndex, steps.length]);

  return (
    <WizardLayout
      steps={steps}
      currentStep={currentStep}
      selectedAreas={(data.primaryObjectOfWork ?? []).flatMap((o) => Object.keys(o))}
      breadcrumbGroup={breadcrumbGroup}
      progressPercentage={progressPercentage}
      activeStep={active}
      isValid={isValid}
      canSubmitDiscovery={canSubmitDiscovery}
      submitting={submitting}
      onStepClick={setCurrentStepIndex}
      onBack={() => setCurrentStepIndex(Math.max(0, currentStep - 1))}
      onSaveExit={handleSaveAndExit}
      onNext={handleNext}
      onValidationChange={handleValidationChange}
      onEdit={handleEdit}
    />
  );
}
